import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCategories, usePost, useUpdateProject } from "../lib/api";
import { type StoredFile, uploadToStorage } from "../lib/storage";
import { validateImage } from "../lib/validators";

interface Attached {
  localId: string;
  name: string;
  progress: number;
  stored?: StoredFile;
  error?: string;
}

export function EditProject() {
  const { id = "" } = useParams();
  const { data: post, isLoading } = usePost(id);
  const { data: categories } = useCategories();
  const update = useUpdateProject(id);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState("");
  const [thumb, setThumb] = useState<{ url: string | null; key: string | null; changed: boolean }>({
    url: null,
    key: null,
    changed: false,
  });
  const [thumbErr, setThumbErr] = useState<string | null>(null);
  const [removeIds, setRemoveIds] = useState<Set<string>>(new Set());
  const [newFiles, setNewFiles] = useState<Attached[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setCategoryId(post.categoryId);
      setDescription(post.description);
      setFeatures(post.features);
      setThumb({ url: post.thumbnailUrl, key: post.thumbnailKey, changed: false });
    }
  }, [post]);

  async function onThumbPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateImage(file);
    if (err) {
      setThumbErr(err);
      return;
    }
    setThumbErr(null);
    try {
      const stored = await uploadToStorage(file);
      setThumb({ url: URL.createObjectURL(file), key: stored.storagePath, changed: true });
    } catch (uploadErr) {
      setThumbErr(uploadErr instanceof Error ? uploadErr.message : "이미지 업로드 실패");
    }
  }

  function toggleRemove(fileId: string) {
    setRemoveIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = "";
    for (const file of picked) {
      const localId = crypto.randomUUID();
      setNewFiles((prev) => [...prev, { localId, name: file.name, progress: 0 }]);
      try {
        const stored = await uploadToStorage(file, (pct) =>
          setNewFiles((prev) =>
            prev.map((f) => (f.localId === localId ? { ...f, progress: pct } : f)),
          ),
        );
        setNewFiles((prev) =>
          prev.map((f) => (f.localId === localId ? { ...f, stored, progress: 100 } : f)),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "업로드 실패";
        setNewFiles((prev) =>
          prev.map((f) => (f.localId === localId ? { ...f, error: message } : f)),
        );
      }
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (title.trim().length === 0) {
      setError("제목을 입력해주세요");
      return;
    }
    if (newFiles.some((f) => !f.stored && !f.error)) {
      setError("파일 업로드가 끝날 때까지 기다려주세요");
      return;
    }
    setError(null);
    try {
      const addFiles = newFiles.map((f) => f.stored).filter((s): s is StoredFile => Boolean(s));
      await update.mutateAsync({
        title: title.trim(),
        description,
        features,
        categoryId,
        ...(thumb.changed ? { thumbnailKey: thumb.key } : {}),
        removeFileIds: [...removeIds],
        addFiles,
      });
      navigate(`/projects/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "수정에 실패했습니다");
    }
  }

  if (isLoading || !post) {
    return <div className="mt-20 text-center font-mono text-sm text-cyan-300/70">불러오는 중…</div>;
  }

  return (
    <section className="mx-auto max-w-2xl fade-up">
      <div className="glass neon-border rounded-2xl p-7">
        <h1 className="neon-text text-2xl font-bold">프로젝트 수정</h1>
        <form className="mt-5 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            제목
            <input
              aria-label="제목"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            카테고리
            <select
              aria-label="카테고리"
              value={categoryId ?? ""}
              onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : null)}
              className="input-cyber rounded-lg px-3 py-2.5"
            >
              <option value="">선택 안 함</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            소개
            <textarea
              aria-label="소개"
              value={description}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-slate-300">
            주요 기능
            <textarea
              aria-label="주요 기능"
              value={features}
              rows={5}
              onChange={(e) => setFeatures(e.target.value)}
              className="input-cyber rounded-lg px-3 py-2.5"
            />
          </label>

          <div className="flex flex-col gap-2">
            <label className="text-sm text-slate-300">
              대표 이미지 (교체)
              <input
                aria-label="대표 이미지"
                type="file"
                accept="image/*"
                onChange={onThumbPick}
                className="mt-1 block w-full text-sm text-slate-400 file:mr-3 file:rounded-lg file:border file:border-cyan-400/40 file:bg-cyan-400/10 file:px-3 file:py-1.5 file:text-cyan-200"
              />
            </label>
            {thumbErr && <p className="text-xs text-fuchsia-400">{thumbErr}</p>}
            {thumb.url && (
              <img
                src={thumb.url}
                alt="대표 이미지 미리보기"
                className="h-32 w-auto rounded-lg border border-cyan-400/20 object-cover"
              />
            )}
          </div>

          {post.files.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-slate-300">기존 파일 (삭제할 파일 선택)</span>
              {post.files.map((f) => (
                <label
                  key={f.id}
                  className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2 text-xs"
                >
                  <span
                    className={
                      removeIds.has(f.id) ? "text-slate-500 line-through" : "text-slate-200"
                    }
                  >
                    {f.originalName}
                  </span>
                  <span className="flex items-center gap-2 text-slate-400">
                    삭제
                    <input
                      type="checkbox"
                      aria-label={`${f.originalName} 삭제`}
                      checked={removeIds.has(f.id)}
                      onChange={() => toggleRemove(f.id)}
                    />
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-sm text-slate-300">
              파일 추가 (최대 2GB)
              <input
                aria-label="파일 추가"
                type="file"
                multiple
                onChange={onPick}
                className="mt-1 block w-full text-sm text-slate-400 file:mr-3 file:rounded-lg file:border file:border-cyan-400/40 file:bg-cyan-400/10 file:px-3 file:py-1.5 file:text-cyan-200"
              />
            </label>
            {newFiles.map((f) => (
              <div key={f.localId} className="rounded-lg border border-slate-800 px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-200">{f.name}</span>
                  <span className="font-mono text-slate-500">
                    {f.error ? "실패" : f.stored ? "완료" : `${f.progress}%`}
                  </span>
                </div>
                {f.error && <p className="mt-1 text-fuchsia-400">{f.error}</p>}
              </div>
            ))}
          </div>

          {error && <p className="text-sm text-fuchsia-400">{error}</p>}
          <button
            type="submit"
            disabled={update.isPending}
            className="btn-neon rounded-lg py-2.5 font-semibold"
          >
            저장하기
          </button>
        </form>
      </div>
    </section>
  );
}
